import {
    Building2,
    ChevronDown,
    LayoutGrid,
    List,
    MessageSquarePlus,
    MoreHorizontal,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Fragment, type ReactNode, useCallback, useMemo } from 'react';

import {
    ActiveFilterChips,
    FilterAddMenu,
    type FilterChip,
    type FilterDimension,
} from '@apps/web/components/filters/filter_bar';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { SearchInput } from '@apps/web/components/ui/search_input';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { ClickToCallLauncher } from '@apps/web/components/voice_calls/click_to_call_launcher';
import { EVENTS } from '@apps/web/lib/events';
import {
    type OfficeConversationCount,
    type OrganizationOffice,
} from '@libraries/typescript/api_client/schema_types';

export type ConversationInboxTab = 'all' | 'unread' | 'pending';

// Queue views for the ownership-enabled inbox. Maps to the list `assignee`
// query param (`me` / `unassigned` / omitted for `all`).
export type ConversationQueueView = 'me' | 'unassigned' | 'all';
// List vs Kanban board layout for the inbox body.
export type ConversationInboxView = 'list' | 'board';
// Read-state filter (null = show both). Maps to the list `unread_only` /
// `read_only` query params.
export type ConversationReadFilter = 'unread' | 'read';
export type ConversationOfficeFilter = {
    readonly officeIds: ReadonlyArray<string>;
    readonly includeUnassigned: boolean;
};

const QUEUE_VIEWS: ReadonlyArray<{
    readonly value: ConversationQueueView;
    readonly label: string;
}> = [
    { value: 'all', label: 'All' },
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'me', label: 'Assigned to me' },
];

const OFFICE_FILTER_DIMENSION_KEY = 'office';
// Sentinel chip value for conversations whose caregiver has no office. Real
// office values are UUIDs, so this never collides with a real office id.
const UNASSIGNED_OFFICE_VALUE = '__unassigned__';

type ConversationListPanelHeaderProps = {
    readonly title?: string | null;
    readonly onMarkAllAsRead?: () => Promise<void>;
    readonly isMarkingAllAsRead: boolean;
    readonly totalUnreadCount: number;
    readonly activeTab?: ConversationInboxTab;
    readonly setActiveTab?: (tab: ConversationInboxTab) => void;
    readonly totalPendingCount?: number;
    readonly pendingFirst?: boolean;
    readonly setPendingFirst?: (enabled: boolean) => void;
    readonly search: string;
    readonly setSearch: (search: string) => void;
    readonly onNewMessage?: () => void;
    readonly officeOptions?: ReadonlyArray<OrganizationOffice>;
    readonly officeFilter?: ConversationOfficeFilter;
    readonly onOfficeFilterChange?: (filter: ConversationOfficeFilter) => void;
    readonly officeConversationCounts?: ReadonlyArray<OfficeConversationCount>;
    readonly unassignedOfficeCount?: number;
    // Conversation ownership (flag `conversation_ownership_enabled`) gates
    // the Assignment filter, board, and assignment-management features. The
    // other list filters remain available to every organization.
    readonly ownershipEnabled?: boolean;
    readonly queueView?: ConversationQueueView;
    readonly setQueueView?: (view: ConversationQueueView) => void;
    readonly inboxView?: ConversationInboxView;
    readonly setInboxView?: (view: ConversationInboxView) => void;
    readonly readFilter?: ConversationReadFilter | null;
    readonly setReadFilter?: (filter: ConversationReadFilter | null) => void;
    readonly careCoordinatorFilter?: ReactNode;
    readonly savedViews?: ReactNode;
};

export function ConversationListPanelHeader({
    title,
    onMarkAllAsRead,
    isMarkingAllAsRead,
    totalUnreadCount,
    activeTab = 'all',
    setActiveTab,
    pendingFirst = false,
    setPendingFirst,
    search,
    setSearch,
    onNewMessage,
    officeOptions,
    officeFilter,
    onOfficeFilterChange,
    officeConversationCounts,
    unassignedOfficeCount,
    ownershipEnabled = false,
    queueView = 'all',
    setQueueView,
    inboxView = 'list',
    setInboxView,
    readFilter = null,
    setReadFilter,
    careCoordinatorFilter,
    savedViews,
}: ConversationListPanelHeaderProps) {
    const posthog = usePostHog();
    const handleTabChange = (value: string) => {
        if (value === 'all' || value === 'unread' || value === 'pending') {
            setActiveTab?.(value);
        }
    };
    const handleQueueChange = (value: string) => {
        if (value === 'me' || value === 'unassigned' || value === 'all') {
            setQueueView?.(value);
            posthog?.capture(EVENTS.CONVERSATION_QUEUE_SWITCHED, {
                queue_view: value,
            });
        }
    };
    const handleInboxViewChange = (value: string) => {
        if (value === 'list' || value === 'board') {
            setInboxView?.(value);
            posthog?.capture(EVENTS.CONVERSATION_INBOX_VIEW_SWITCHED, {
                inbox_view: value,
            });
        }
    };
    const showOfficeFilter = !!officeOptions && !!onOfficeFilterChange;
    const showAssignmentFilter = ownershipEnabled && !!setQueueView;
    const showReadFilter = !!setReadFilter;
    const showCareCoordinatorFilter = !!careCoordinatorFilter;
    const showFunctionalFilters =
        showAssignmentFilter ||
        showReadFilter ||
        showOfficeFilter ||
        showCareCoordinatorFilter;
    const showInboxViewOptions = ownershipEnabled && !!setInboxView;
    const showListOptions =
        showInboxViewOptions || !!setPendingFirst || !!onMarkAllAsRead;

    const officeCountById = useMemo(() => {
        const counts = new Map<string, number>();
        for (const entry of officeConversationCounts ?? []) {
            counts.set(entry.office_id, entry.count);
        }
        return counts;
    }, [officeConversationCounts]);

    const officeDimension = useMemo<FilterDimension>(
        () => ({
            key: OFFICE_FILTER_DIMENSION_KEY,
            label: 'Office',
            icon: Building2,
            multiSelect: true,
            searchable: true,
            options: [
                ...(officeOptions ?? []).map((office) => ({
                    value: office.id,
                    label: office.display_name,
                    count: officeCountById.get(office.id) ?? 0,
                })),
                ...(onOfficeFilterChange
                    ? [
                          {
                              value: UNASSIGNED_OFFICE_VALUE,
                              label: 'No office',
                              count: unassignedOfficeCount ?? 0,
                          },
                      ]
                    : []),
            ],
        }),
        [
            officeOptions,
            officeCountById,
            unassignedOfficeCount,
            onOfficeFilterChange,
        ],
    );

    const officeChips = useMemo<Array<FilterChip>>(() => {
        const values = [
            ...(officeFilter?.officeIds ?? []),
            ...(officeFilter?.includeUnassigned
                ? [UNASSIGNED_OFFICE_VALUE]
                : []),
        ];
        return values.length > 0
            ? [{ dimension: OFFICE_FILTER_DIMENSION_KEY, values }]
            : [];
    }, [officeFilter]);

    const filterDimensions = useMemo<Array<FilterDimension>>(
        () => (showOfficeFilter ? [officeDimension] : []),
        [showOfficeFilter, officeDimension],
    );

    const handleOfficeFilterChange = useCallback(
        (nextChips: ReadonlyArray<FilterChip>) => {
            const chip = nextChips.find(
                (candidate) =>
                    candidate.dimension === OFFICE_FILTER_DIMENSION_KEY,
            );
            const values = chip?.values ?? [];
            const nextOfficeIds = values.filter(
                (value) => value !== UNASSIGNED_OFFICE_VALUE,
            );
            const nextUnassigned = values.includes(UNASSIGNED_OFFICE_VALUE);
            onOfficeFilterChange?.({
                officeIds: nextOfficeIds,
                includeUnassigned: nextUnassigned,
            });
        },
        [onOfficeFilterChange],
    );

    const handleOfficeValueToggle = useCallback(
        (value: string) => {
            const currentValues = officeChips[0]?.values ?? [];
            const nextValues = currentValues.includes(value)
                ? currentValues.filter((currentValue) => currentValue !== value)
                : [...currentValues, value];
            handleOfficeFilterChange(
                nextValues.length > 0
                    ? [
                          {
                              dimension: OFFICE_FILTER_DIMENSION_KEY,
                              values: nextValues,
                          },
                      ]
                    : [],
            );
        },
        [handleOfficeFilterChange, officeChips],
    );

    const handleReadChange = (value: string) => {
        const nextReadFilter =
            value === 'unread' || value === 'read' ? value : null;
        setReadFilter?.(nextReadFilter);
        posthog?.capture(EVENTS.CONVERSATION_READ_FILTER_CHANGED, {
            read_filter: nextReadFilter ?? 'all',
        });
    };

    const isUnrespondedSelected = activeTab === 'pending';
    const queueViewLabel =
        QUEUE_VIEWS.find((view) => view.value === queueView)?.label ?? 'All';
    const readFilterLabel =
        readFilter === 'unread'
            ? 'Unread'
            : readFilter === 'read'
              ? 'Read'
              : 'All';
    const selectedOfficeValues = officeChips[0]?.values ?? [];
    const officeFilterLabel = (() => {
        if (selectedOfficeValues.length === 0) {
            return 'All';
        }
        if (selectedOfficeValues.length > 1) {
            return `${selectedOfficeValues.length} selected`;
        }
        const [selectedOfficeValue] = selectedOfficeValues;
        return (
            officeDimension.options.find(
                (option) => option.value === selectedOfficeValue,
            )?.label ?? 'All'
        );
    })();
    const inboxPillClassName =
        'h-6 rounded-full border-transparent bg-secondary px-2 text-xs font-medium text-secondary-foreground hover:bg-secondary/80 has-[>svg]:px-2';
    const inboxPillActiveClassName =
        'h-6 rounded-full border-transparent bg-primary px-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground has-[>svg]:px-2';
    const compactFilterClassName =
        'h-7 shrink-0 gap-1 rounded-md px-2 text-xs font-medium shadow-none has-[>svg]:px-2';

    return (
        <div className="flex-shrink-0 space-y-3 px-3 pt-4 pb-3">
            <div className="flex items-center justify-between gap-2 px-1">
                {title ? (
                    <h2 className="truncate text-lg font-semibold">{title}</h2>
                ) : null}
                <div className="flex shrink-0 items-center">
                    <ClickToCallLauncher />
                    {onNewMessage ? (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <Button
                                    aria-label="New message"
                                    className="h-11 w-11 shrink-0 shadow-none md:h-8 md:w-8"
                                    onClick={onNewMessage}
                                    size="icon"
                                    type="button"
                                    variant="ghost"
                                >
                                    <MessageSquarePlus className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>New message</TooltipContent>
                        </Tooltip>
                    ) : null}
                    {showListOptions ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild={true}>
                                <Button
                                    aria-label="Conversation list options"
                                    className="h-11 w-11 shrink-0 shadow-none md:h-8 md:w-8"
                                    size="icon"
                                    type="button"
                                    variant="ghost"
                                >
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {showInboxViewOptions ? (
                                    <DropdownMenuRadioGroup
                                        onValueChange={handleInboxViewChange}
                                        value={inboxView}
                                    >
                                        <DropdownMenuRadioItem value="list">
                                            <List className="mr-2 h-4 w-4" />
                                            List view
                                        </DropdownMenuRadioItem>
                                        <DropdownMenuRadioItem value="board">
                                            <LayoutGrid className="mr-2 h-4 w-4" />
                                            Board view
                                        </DropdownMenuRadioItem>
                                    </DropdownMenuRadioGroup>
                                ) : null}
                                {setPendingFirst ? (
                                    <DropdownMenuCheckboxItem
                                        checked={pendingFirst}
                                        onCheckedChange={(checked) =>
                                            setPendingFirst(checked === true)
                                        }
                                    >
                                        Pending approvals first
                                    </DropdownMenuCheckboxItem>
                                ) : null}
                                {onMarkAllAsRead ? (
                                    <Fragment>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            disabled={
                                                isMarkingAllAsRead ||
                                                totalUnreadCount === 0
                                            }
                                            onSelect={() => {
                                                void onMarkAllAsRead();
                                            }}
                                        >
                                            {isMarkingAllAsRead ? (
                                                <Spinner className="mr-2" />
                                            ) : null}
                                            Mark all as read
                                        </DropdownMenuItem>
                                    </Fragment>
                                ) : null}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : null}
                </div>
            </div>
            {savedViews}
            {showFunctionalFilters ? (
                <div
                    className="flex flex-wrap items-center gap-1.5"
                    data-testid="conversation-functional-filters"
                >
                    {showAssignmentFilter ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild={true}>
                                <Button
                                    aria-label={`Assignment: ${queueViewLabel}`}
                                    className={compactFilterClassName}
                                    size="sm"
                                    type="button"
                                    variant="outline"
                                >
                                    <span>Assignment: {queueViewLabel}</span>
                                    <ChevronDown className="size-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuRadioGroup
                                    onValueChange={handleQueueChange}
                                    value={queueView}
                                >
                                    {QUEUE_VIEWS.map((view) => (
                                        <DropdownMenuRadioItem
                                            key={view.value}
                                            value={view.value}
                                        >
                                            {view.label}
                                        </DropdownMenuRadioItem>
                                    ))}
                                </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : null}
                    {showReadFilter ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild={true}>
                                <Button
                                    aria-label={`Read: ${readFilterLabel}`}
                                    className={compactFilterClassName}
                                    size="sm"
                                    type="button"
                                    variant="outline"
                                >
                                    <span>Read: {readFilterLabel}</span>
                                    <ChevronDown className="size-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuRadioGroup
                                    onValueChange={handleReadChange}
                                    value={readFilter ?? 'all'}
                                >
                                    <DropdownMenuRadioItem value="all">
                                        All
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="unread">
                                        Unread
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="read">
                                        Read
                                    </DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : null}
                    {showOfficeFilter ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild={true}>
                                <Button
                                    aria-label={`Office: ${officeFilterLabel}`}
                                    className={compactFilterClassName}
                                    size="sm"
                                    type="button"
                                    variant="outline"
                                >
                                    <span className="max-w-28 truncate">
                                        Office: {officeFilterLabel}
                                    </span>
                                    <ChevronDown className="size-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuItem
                                    onSelect={() =>
                                        handleOfficeFilterChange([])
                                    }
                                >
                                    All offices
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {officeDimension.options.map((option) => (
                                    <DropdownMenuCheckboxItem
                                        checked={selectedOfficeValues.includes(
                                            option.value,
                                        )}
                                        key={option.value}
                                        onCheckedChange={() =>
                                            handleOfficeValueToggle(
                                                option.value,
                                            )
                                        }
                                        onSelect={(event) =>
                                            event.preventDefault()
                                        }
                                    >
                                        <span className="min-w-0 flex-1 truncate">
                                            {option.label}
                                        </span>
                                        {option.count !== undefined ? (
                                            <span
                                                aria-hidden={true}
                                                className="text-muted-foreground ml-auto text-xs"
                                            >
                                                {option.count}
                                            </span>
                                        ) : null}
                                    </DropdownMenuCheckboxItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : null}
                    {careCoordinatorFilter}
                </div>
            ) : (
                <Fragment>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <Button
                            className={
                                activeTab === 'unread'
                                    ? inboxPillActiveClassName
                                    : inboxPillClassName
                            }
                            onClick={() => {
                                handleTabChange(
                                    activeTab === 'unread' ? 'all' : 'unread',
                                );
                            }}
                            size="sm"
                            type="button"
                            variant="outline"
                        >
                            Unread
                        </Button>
                        <Button
                            className={
                                isUnrespondedSelected
                                    ? inboxPillActiveClassName
                                    : inboxPillClassName
                            }
                            onClick={() => {
                                handleTabChange(
                                    activeTab === 'pending' ? 'all' : 'pending',
                                );
                            }}
                            size="sm"
                            type="button"
                            variant="outline"
                        >
                            Unresponded
                        </Button>
                        {filterDimensions.length > 0 ? (
                            <FilterAddMenu
                                chips={officeChips}
                                className={inboxPillClassName}
                                dimensions={filterDimensions}
                                label="Filter"
                                onChange={handleOfficeFilterChange}
                            />
                        ) : null}
                    </div>
                    {filterDimensions.length > 0 ? (
                        <ActiveFilterChips
                            chips={officeChips}
                            dimensions={filterDimensions}
                            onChange={handleOfficeFilterChange}
                        />
                    ) : null}
                </Fragment>
            )}
            <SearchInput
                onValueChange={setSearch}
                placeholder="Search conversations…"
                value={search}
            />
        </div>
    );
}
